/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import {
  Box, Typography, Grid, Card, CardContent, List, ListItem, ListItemText,
  Skeleton, Button, Chip, Avatar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, LinearProgress, Divider,
} from '@mui/material';
import {
  DirectionsCar, Person, AttachMoney, Star, Warning, CheckCircle,
  Refresh, MoreVert, Notifications, Assessment,
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell,
} from 'recharts';
import { useState, useEffect } from 'react';
import { getAnalytics, getDrivers, getTrips, getDriverRevenueLeaderboard, type DriverRevenueRow } from '../services/graphql';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/format';

function Dashboard() {
  const { hasRole } = useAuth();

  const canSeeRevenue    = hasRole('SuperAdmin', 'Finance');
  const canSeeAnalytics  = hasRole('SuperAdmin', 'Operations', 'Finance');
  const canSeeDrivers    = hasRole('SuperAdmin', 'Operations', 'CustomerService', 'TechSupport');
  const canSeeTrips      = hasRole('SuperAdmin', 'Operations', 'CustomerService', 'Finance');
  const canManageDrivers = hasRole('SuperAdmin', 'Operations');
  const canSeeUsers      = hasRole('SuperAdmin', 'CustomerService');

  const [analytics, setAnalytics] = useState<any>(null);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [trips, setTrips] = useState<any[]>([]);
  const [topDrivers, setTopDrivers] = useState<DriverRevenueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchData = async () => {
    try {
      const [analyticsResult, driversResult, tripsResult] = await Promise.allSettled([
        getAnalytics(),
        canSeeDrivers ? getDrivers() : Promise.resolve([]),
        canSeeTrips   ? getTrips()   : Promise.resolve([]),
      ]);

      if (analyticsResult.status === 'fulfilled') {
        setAnalytics(analyticsResult.value);
      } else {
        console.error('Failed to load analytics:', analyticsResult.reason);
      }

      if (driversResult.status === 'fulfilled') {
        setDrivers(driversResult.value);
      } else {
        console.error('Failed to load drivers:', driversResult.reason);
      }

      if (tripsResult.status === 'fulfilled') {
        setTrips(tripsResult.value);
      } else {
        console.error('Failed to load trips:', tripsResult.reason);
      }

      // Minimal top-earning drivers (this month) — revenue-gated
      if (canSeeRevenue) {
        try {
          const board = await getDriverRevenueLeaderboard({ period: 'MONTH', order: 'TOP', limit: 5 });
          setTopDrivers(board.items);
        } catch (e) {
          console.error('Failed to load driver revenue leaderboard:', e);
        }
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const StatCard = ({ title, value, icon, color }: any) => (
    <Card elevation={0} style={{ border: '1px solid #dedede' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">{title}</Typography>
            <Typography variant="h4">{value}</Typography>
          </Box>
          <Box sx={{ color, fontSize: 40 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading || !analytics) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Dashboard Overview</Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card elevation={0} style={{ border: '1px solid #dedede' }}>
                <CardContent>
                  <Skeleton variant="text" width={100} height={20} />
                  <Skeleton variant="text" width={80} height={40} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card elevation={0} style={{ border: '1px solid #dedede' }}>
              <CardContent>
                <Skeleton variant="text" width={200} height={30} />
                <Skeleton variant="rectangular" width="100%" height={300} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={0} style={{ border: '1px solid #dedede' }}>
              <CardContent>
                <Skeleton variant="text" width={150} height={30} />
                <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto' }} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  const { totalTrips, activeDrivers, totalUsers, totalRevenue, cashRevenue, nonCashRevenue, averageRating, tripsByHour, revenueByDay, driverStatus, recentActivity } = analytics;

  const TERMINAL_STATUSES = ['COMPLETED', 'CANCELLED', 'CANCELLED_BY_RIDER', 'CANCELLED_BY_DRIVER', 'EXPIRED', 'RIDER_NO_SHOW', 'DRIVER_NO_SHOW', 'NO_DRIVERS_AVAILABLE'];
  const isActiveTrip      = (t: any) => !TERMINAL_STATUSES.includes((t.status || '').toUpperCase());
  const activeTrips       = trips.filter(isActiveTrip).length;
  const pendingDrivers    = drivers.filter(d => d.status === 'pending').length;
  const onlineDrivers     = drivers.filter(d => d.status === 'active').length;
  const completedToday    = trips.filter(t => (t.status || '').toUpperCase() === 'COMPLETED' && t.completedAt && new Date(t.completedAt).toDateString() === new Date().toDateString()).length;
  const recentDrivers     = drivers.slice(0, 5);
  const activeTripsData   = trips.filter(isActiveTrip).slice(0, 5);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>Dashboard Overview</Typography>
          <Typography variant="body2" color="textSecondary">Last updated: {lastUpdated.toLocaleTimeString()}</Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData} disabled={loading}>Refresh</Button>
          {canSeeAnalytics && (
            <Button variant="contained" startIcon={<Assessment />} href="/analytics">View Analytics</Button>
          )}
        </Box>
      </Box>

      {/* KPI Cards — filtered by role */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Total Trips" value={totalTrips.toLocaleString()} icon={<DirectionsCar />} color="#2196f3" />
        </Grid>
        {canSeeDrivers && (
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Active Drivers" value={activeDrivers} icon={<Person />} color="#4caf50" />
          </Grid>
        )}
        {canSeeUsers && (
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Total Users" value={totalUsers} icon={<Person />} color="#ff9800" />
          </Grid>
        )}
        {canSeeRevenue && (
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Revenue" value={formatCurrency(totalRevenue)} icon={<AttachMoney />} color="#9c27b0" />
          </Grid>
        )}
        {canSeeRevenue && (
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Cash Revenue" value={formatCurrency(cashRevenue ?? 0)} icon={<AttachMoney />} color="#ed6c02" />
          </Grid>
        )}
        {canSeeRevenue && (
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Non-Cash Revenue" value={formatCurrency(nonCashRevenue ?? 0)} icon={<AttachMoney />} color="#1565c0" />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Avg Rating" value={averageRating} icon={<Star />} color="#f44336" />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Trips by Hour */}
        <Grid item xs={12} md={canSeeDrivers ? 8 : 12}>
          <Card elevation={0} style={{ border: '1px solid #dedede' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Trips by Hour (Today)</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={tripsByHour}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" /><YAxis /><Tooltip />
                  <Line type="monotone" dataKey="trips" stroke="#2196f3" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Driver Status pie — only if can see drivers */}
        {canSeeDrivers && (
          <Grid item xs={12} md={4}>
            <Card elevation={0} style={{ border: '1px solid #dedede' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Driver Status</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={driverStatus} cx="50%" cy="50%" outerRadius={80} dataKey="count"
                      label={({ status, count }: any) => `${status}: ${count}`}>
                      {driverStatus.map((entry: any, i: number) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Revenue by Day — Finance + SuperAdmin only */}
        {canSeeRevenue && (
          <Grid item xs={12} md={8}>
            <Card elevation={0} style={{ border: '1px solid #dedede' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Revenue by Day (This Week)</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" /><YAxis />
                    <Tooltip formatter={(v: any) => [formatCurrency(Number(v)), 'Revenue']} />
                    <Bar dataKey="revenue" fill="#4caf50" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Top Drivers by Revenue (This Month) — minimal, revenue-gated */}
        {canSeeRevenue && (
          <Grid item xs={12} md={4}>
            <Card elevation={0} style={{ border: '1px solid #dedede' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Top Drivers (This Month)</Typography>
                  {canSeeAnalytics && <Button size="small" href="/analytics">View All</Button>}
                </Box>
                <List dense>
                  {topDrivers.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No revenue data yet</Typography>
                  ) : topDrivers.map((d, i) => (
                    <ListItem key={d.driverId} sx={{ px: 0 }} divider>
                      <ListItemText
                        primary={`#${i + 1} ${d.driverName || d.driverId.slice(0, 8)}`}
                        secondary={`${d.tripCount} trips`}
                      />
                      <Typography variant="body2" fontWeight="bold">{formatCurrency(d.grossRevenue)}</Typography>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recent Activity */}
        <Grid item xs={12} md={canSeeRevenue ? 4 : 6}>
          <Card elevation={0} style={{ border: '1px solid #dedede' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Activity</Typography>
              <List>
                {recentActivity.map((a: any, i: number) => (
                  <ListItem key={i} divider>
                    <ListItemText primary={a.action} secondary={a.time} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Active Trips table — roles that can see trips */}
        {canSeeTrips && (
          <Grid item xs={12} md={8}>
            <Card elevation={0} style={{ border: '1px solid #dedede' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Active Trips ({activeTrips})</Typography>
                  <Button size="small" href="/trips">View All</Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Trip ID</TableCell>
                        <TableCell>Driver</TableCell>
                        <TableCell>Passenger</TableCell>
                        <TableCell>Status</TableCell>
                        {canSeeRevenue && <TableCell>Fare</TableCell>}
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeTripsData.length > 0 ? activeTripsData.map((trip: any) => (
                        <TableRow key={trip.id}>
                          <TableCell><Typography variant="body2" fontFamily="monospace">{trip.id.slice(-8)}</Typography></TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar sx={{ width: 24, height: 24 }}>{trip.driverName?.charAt(0) || 'D'}</Avatar>
                              <Typography variant="body2">{trip.driverName || 'Unknown'}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell><Typography variant="body2">{trip.passengerName || 'Unknown'}</Typography></TableCell>
                          <TableCell><Chip label={trip.status.toUpperCase()} color="info" size="small" /></TableCell>
                          {canSeeRevenue && <TableCell><Typography variant="body2" fontWeight="bold">{formatCurrency(trip.finalFare || trip.fareEstimate || 0)}</Typography></TableCell>}
                          <TableCell><IconButton size="small"><MoreVert /></IconButton></TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={canSeeRevenue ? 6 : 5} align="center">
                            <Typography color="textSecondary">No active trips</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Driver Overview — roles that can see drivers */}
        {canSeeDrivers && (
          <Grid item xs={12} md={4}>
            <Card elevation={0} style={{ border: '1px solid #dedede' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Driver Overview</Typography>
                  {canManageDrivers && <Button size="small" href="/drivers">Manage</Button>}
                </Box>
                <Box mb={3}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Online Drivers</Typography>
                    <Typography variant="body2" fontWeight="bold">{onlineDrivers}/{drivers.length}</Typography>
                  </Box>
                  <LinearProgress variant="determinate"
                    value={drivers.length > 0 ? (onlineDrivers / drivers.length) * 100 : 0}
                    sx={{ height: 8, borderRadius: 4 }} />
                </Box>
                {pendingDrivers > 0 && (
                  <Box display="flex" alignItems="center" gap={1} mb={2} p={1}
                    sx={{ backgroundColor: 'warning.light', borderRadius: 1 }}>
                    <Warning color="warning" />
                    <Typography variant="body2">{pendingDrivers} drivers pending approval</Typography>
                  </Box>
                )}
                <Typography variant="subtitle2" mb={1}>Recent Drivers</Typography>
                <List dense>
                  {recentDrivers.map((driver: any) => (
                    <ListItem key={driver.id} sx={{ px: 0 }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 1 }}>{driver.name.charAt(0)}</Avatar>
                      <ListItemText
                        primary={driver.name}
                        secondaryTypographyProps={{ component: 'span' }}
                        secondary={
                          <Box component="span" display="flex" alignItems="center" gap={1}>
                            <Chip label={driver.status} size="small" color={driver.status === 'active' ? 'success' : 'default'} />
                            <Typography variant="caption">⭐ {driver.rating}</Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Quick Actions — filtered by role */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} style={{ border: '1px solid #dedede' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quick Actions</Typography>
              <Grid container spacing={2}>
                {canManageDrivers && (
                  <Grid item xs={6}>
                    <Button fullWidth variant="outlined" startIcon={<Person />} href="/drivers" sx={{ justifyContent: 'flex-start' }}>
                      Manage Drivers
                    </Button>
                  </Grid>
                )}
                {canSeeTrips && (
                  <Grid item xs={6}>
                    <Button fullWidth variant="outlined" startIcon={<DirectionsCar />} href="/trips" sx={{ justifyContent: 'flex-start' }}>
                      View Trips
                    </Button>
                  </Grid>
                )}
                {canSeeAnalytics && (
                  <Grid item xs={6}>
                    <Button fullWidth variant="outlined" startIcon={<Assessment />} href="/analytics" sx={{ justifyContent: 'flex-start' }}>
                      Analytics
                    </Button>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined" startIcon={<Notifications />} href="/status" sx={{ justifyContent: 'flex-start' }}>
                    System Status
                  </Button>
                </Grid>
              </Grid>
              {completedToday > 0 && (
                <Box mt={2} p={2} sx={{ backgroundColor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.dark">🎉 {completedToday} trips completed today!</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
